import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { PassengerFare } from '../../BookingWizardData/BaseBookingWizardData';

/** How many passengers of each type are on the booking, keyed by the type's key (A-H). */
export type PassengerCounts = Record<string, number>;

/**
 * What this booking's passengers cost in one class.
 *
 * <p>The counts are what make the figure a price rather than a sample. A class's fares differ by
 * passenger type, so "Business costs $85" is only true of an adult travelling alone — two adults and
 * a child is a different number, and it is the one the booker is deciding between.</p>
 *
 * <p>Passengers only. The card's headline still carries the whole booking including any vehicle, so
 * the difference between two cells is exactly the difference the class makes.</p>
 */
export const classPartyTotal = (classPrice: TripClassPrice, counts: PassengerCounts): number =>
	Object.entries(counts).reduce(
		(total, [typeKey, count]) => total + count * (classPrice.fares?.[typeKey] ?? 0),
		0,
	);

/**
 * What this party costs on a sailing with every traveller left in their own fare type.
 *
 * <p>The figure a split booking needs, and the one no column can give it. Each cell answers "what if
 * everybody moved here", which is the right answer to a click on that cell and the wrong one for a
 * party that is staying as it is — a booking with one passenger in Business reads as entirely
 * Economy or entirely Business, and is neither.</p>
 *
 * <p><b>Null where the sailing does not sell one of the fare types in play</b>, or where a traveller
 * has none yet. The party cannot cross as it stands, so there is no price for staying as they are —
 * and a figure quietly computed from the fare types that did match would be short by a passenger.</p>
 */
export const splitPartyTotal = (
	classPrices: TripClassPrice[],
	fares: PassengerFare[],
): number | null => {
	if (fares.length === 0) {
		return null;
	}

	let total = 0;
	for (const fare of fares) {
		const classPrice = fare.passengerClassId
			? classPrices.find(x => x.passengerClassId === fare.passengerClassId)
			: undefined;
		if (!classPrice) {
			return null;
		}
		total += classPrice.fares?.[fare.typeKey] ?? 0;
	}

	return total;
};

/** The cheapest class for this party, which is what a "from" figure on the card means. */
export const cheapestClassTotal = (
	classPrices: TripClassPrice[],
	counts: PassengerCounts,
): number | null =>
	classPrices.length === 0
		? null
		: Math.min(...classPrices.map(x => classPartyTotal(x, counts)));

/** The cheapest this party can travel in one class, on a given day. */
export interface DayClassFare {
	passengerClassId: string;
	name: string;
	colour?: string | null;
	/** Position in the class list, which decides the fallback colour. */
	index: number;
	price: number;
}

/**
 * The cheapest fare per class across a day's sailings.
 *
 * <p>What a day tab should answer once a vessel has classes. A single "from $110" for the whole day
 * is the cheapest way to travel at all, which on a three-class vessel tells a booker comparing
 * Premium across a week nothing they asked — and it duplicates, less well, what the fare columns
 * below already say.</p>
 *
 * <p>A class is listed only where some sailing that day actually prices it, so a day whose vessel
 * does not offer Premium does not show an empty column for it.</p>
 */
export const cheapestByClassForDay = (
	tripIds: string[],
	classPricesByTrip: { [tripId: string]: TripClassPrice[] },
	counts: PassengerCounts,
): DayClassFare[] => {
	const cheapest = new Map<string, DayClassFare>();
	// Order comes from the first sailing that has classes, so the tabs, the header and the cells all
	// list them the same way.
	const order = tripIds
		.map(id => classPricesByTrip[id] ?? [])
		.find(prices => prices.length > 0) ?? [];

	order.forEach((classPrice, index) => {
		const prices = tripIds
			.map(id => (classPricesByTrip[id] ?? [])
				.find(x => x.passengerClassId === classPrice.passengerClassId))
			.filter((x): x is TripClassPrice => !!x)
			.map(x => classPartyTotal(x, counts));

		if (prices.length === 0) {
			return;
		}

		cheapest.set(classPrice.passengerClassId, {
			passengerClassId: classPrice.passengerClassId,
			name: classPrice.name,
			colour: classPrice.colour,
			index,
			price: Math.min(...prices),
		});
	});

	return [...cheapest.values()];
};

/**
 * The cheapest single head on this booking, in one class — the figure behind a "from $x pp".
 *
 * <p>A number rather than a label, because this file is imported by the unit tests, which run
 * without a DOM: <c>formatPriceDisplay</c> reaches <c>Store</c>, <c>Store</c> reaches
 * <c>window</c>, and the suite cannot load the module at all. Formatting lives in
 * <c>fareLabels.ts</c> beside the components that do have a DOM.</p>
 *
 * <p>Null where the site sells no passenger type this class has a fare for.</p>
 */
export const cheapestHeadFare = (
	classPrice: TripClassPrice,
	counts: PassengerCounts,
): number | null => {
	const relevant = Object.keys(counts).length > 0
		? Object.keys(counts).map(key => classPrice.fares?.[key] ?? 0)
		: Object.values(classPrice.fares ?? {});
	const payable = relevant.filter(x => x > 0);

	return payable.length === 0 ? null : Math.min(...payable);
};
